"use client";

import * as React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { useIsAuthPage } from "@/hooks/use-is-auth-page";

type CommandBarContextType = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
};

const CommandBarContext = createContext<CommandBarContextType | undefined>(undefined);

export function CommandBarProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const isAuthPage = useIsAuthPage();

  // Handle keyboard shortcut (Cmd+K or Ctrl+K). Disabled on auth pages —
  // same routes where the sidebar is hidden — and closed if still open when
  // navigating onto one.
  useEffect(() => {
    if (isAuthPage) {
      setIsOpen(false);
      return;
    }

    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [isAuthPage]);

  return React.createElement(
    CommandBarContext.Provider,
    { value: { isOpen, setIsOpen } },
    children,
  );
}

export function useCommandBar() {
  const context = useContext(CommandBarContext);

  if (context === undefined) {
    throw new Error("useCommandBar must be used within a CommandBarProvider");
  }

  return context;
}
